<script lang="ts">
	import { CalcMethod } from '../types/enums'
	import { t } from '../../i18n/i18n'
	import { type Lot } from '../optimizer/lot'
	import OfferTypeCheckbox from './OfferTypeCheckbox.svelte'
	import OfferFactsCard from './OfferFactsCard.svelte'
	import OfferPriceCard from './OfferPriceCard.svelte'
	import { EmptyOffer, type OfferDesignatedEntity, type Offers, OfferState } from './types/offer'
	import { type RequestDto, RequestType, RequestedEntityType } from './types/requestDto'
	import TextArea from '../atoms/TextArea.svelte'
	import {
		beforeUpdate,
		createEventDispatcher,
		getContext,
		hasContext,
		onDestroy,
		onMount
	} from 'svelte'
	import AgbButton from '../organisms/AgbButton.svelte'
	import SecondaryButton, { SecondaryBtnColors } from '../atoms/SecondaryButton.svelte'
	import { PrimaryBtnColors } from '../atoms/PrimaryButton.svelte'
	import type { OfferDto, UpdateOfferRequest } from './types/updateOfferRequest'
	import { fetchUtils } from '../utils/fetchUtils'
	import { DefaultNotification, NotificationType } from '../types/notification'
	import { addNotification } from '../stores'
	import OfferFactsPlace from './OfferFactsPlace.svelte'
	import { writable, type Writable } from 'svelte/store'
	import type { PubPageParams } from './types/pubPageParams'
	import { isEmptyOffer } from './offerUtils'
	import { isAnyOfferAccepted } from './offerUtils.js'
	import { jwt } from '../utils/jwt'
	import { DefaultLabelFontProps } from '../types/fontProps'
	import Checkbox from '../atoms/Checkbox.svelte'
	import { UserPermission } from '../types/enums'
	import { hasPermissions } from '../menubar/Permission.svelte'

	const dispatch = createEventDispatcher()
	const request: Writable<RequestDto> = hasContext('request')
		? getContext('request')
		: writable({} as RequestDto)
	const urlParams: PubPageParams | null = hasContext('urlParams') ? getContext('urlParams') : null
	const lot: Writable<Lot> = hasContext('lot') ? getContext('lot') : writable({} as Lot)
	const offers: Writable<Offers> = hasContext('offers')
		? getContext('offers')
		: writable({
				material: EmptyOffer(RequestType.MATERIAL),
				materialWithTransport: EmptyOffer(RequestType.MATERIAL_WITH_TRANSPORT),
				transport: EmptyOffer(RequestType.TRANSPORT)
			})
	const offerDesignatedEntity: Writable<OfferDesignatedEntity | null> = hasContext(
		'offerDesignatedEntity'
	)
		? getContext('offerDesignatedEntity')
		: writable(null)

	export let hideAgbCheckbox: boolean = false

	let materialWithTranspSelected: boolean = false
	let materialSelected: boolean = false
	let transportSelected: boolean = false
	let agbAccepted: boolean = false
	let { requestPublicId, offerUserPublicId, requestedEntityId, requestedEntityType } =
		urlParams ?? ({} as PubPageParams)

	let counterOfferSent: boolean = false

	let createPopMaterial: boolean = false

	$: offerHasMaterialProfile = !!$offers.material.hasMaterialProfile
	$: designatedEntityIsPop = $offerDesignatedEntity?.type === RequestedEntityType.POP
	$: designatedEntityHasMaterialProfile = !!$offerDesignatedEntity?.hasMaterialProfile
	/**
	 * decides which label the createPopMaterial checkbox shows: "update the profile price" when
	 * the selected Standort already has a material profile, or "create a profile" when it does
	 * not.
	 *
	 * there are two possible sources for this information, and they can disagree.
	 * offer.hasMaterialProfile is a snapshot from the server that is taken at page load and never
	 * updated afterwards. $offerDesignatedEntity is rewritten by OfferFactsPlace.onPopSelect every
	 * time the user picks a Standort, so it is always current. a selected POP therefore takes
	 * precedence, and the page-load snapshot is only used as a fallback while no POP is selected
	 * (for example with the LOT seed from OptimizerPage, which carries no profile flag at all).
	 * combining the two sources with a logical OR would be wrong, because the stale snapshot
	 * could override the state of the Standort the user just picked
	 */
	$: showUpdateProfileLabel = designatedEntityIsPop
		? designatedEntityHasMaterialProfile
		: offerHasMaterialProfile

	$: createPopMaterialLabel = showUpdateProfileLabel
		? $t('UI.model.updateProfilePriceCheckbox')
		: $t('UI.model.addProfileCheckbox')

	/**
	 * the checkbox is only usable when the offer has an explicit Standort id, because the backend
	 * validates offerLocationId before saving the offer and rejects createPopMaterial without it
	 * (OptimizerApiFacade.updateOrCreateCounterOffer). the only exception are legacy offers that
	 * were saved with the flag set and an address string instead of an id: for those the checkbox
	 * stays visible and enabled, so the user can see the saved value and is able to opt out
	 */
	$: createPopMaterialAllowed = !!(
		$offers.material.offerLocationId ||
		($offers.material.offerLocation?.trim() && $offers.material.createPopMaterial)
	)

	// reset a checked state that is no longer allowed, so it cannot be submitted
	$: if (!createPopMaterialAllowed && createPopMaterial) {
		createPopMaterial = false
	}

	const checkWithTransport = () => (materialWithTranspSelected = true)
	const checkMaterial = () => (materialSelected = true)
	const checkTransport = () => (transportSelected = true)

	let mounted: boolean = false

	// only execute this once before the first mount, we must do it before the children are mounted
	beforeUpdate(() => {
		if (!mounted) {
			materialSelected =
				!!(
					$offers.material &&
					$offers.material.offerState !== OfferState.DRAFT &&
					!isEmptyOffer($offers.material, RequestType.MATERIAL)
				) || false
			materialWithTranspSelected =
				!!(
					$offers.materialWithTransport &&
					$offers.materialWithTransport.offerState !== OfferState.DRAFT &&
					!isEmptyOffer($offers.materialWithTransport, RequestType.MATERIAL_WITH_TRANSPORT)
				) || false
			transportSelected =
				!!(
					$offers.transport &&
					$offers.transport.offerState !== OfferState.DRAFT &&
					!isEmptyOffer($offers.transport, RequestType.TRANSPORT)
				) || false
			createPopMaterial = $offers.material?.createPopMaterial ?? false
		}
	})
	onMount(() => {
		// needs to be set such that we do not update the selected values before every update, before the first mount
		// this is necessary, because in the price we adapt the offer.facts.kickback, and then it does not match EmptyOffer
		mounted = true
	})

	// when we sent a counteroffer, but one of the offers is not selected to be submitted to the server, we need to reset that offer
	// if we cancel the edit, we reload the data from the server, since the object can be edited and the checkbox can be (un)checked
	// by reloading the data from the server,we can omit a deep equal check and also saving states of the offers objects before and after edit
	onDestroy(() => {
		if (!counterOfferSent) {
			offers.update((o) => ({
				...o,
				material: EmptyOffer(RequestType.MATERIAL),
				materialWithTransport: EmptyOffer(RequestType.MATERIAL_WITH_TRANSPORT),
				transport: EmptyOffer(RequestType.TRANSPORT)
			}))
			dispatch('offerChanged')
			return
		} else {
			if (!materialSelected) {
				offers.update((o) => ({ ...o, material: EmptyOffer(RequestType.MATERIAL) }))
			}
			if (!materialWithTranspSelected) {
				offers.update((o) => ({
					...o,
					materialWithTransport: EmptyOffer(RequestType.MATERIAL_WITH_TRANSPORT)
				}))
			}
			if (!transportSelected) {
				offers.update((o) => ({ ...o, transport: EmptyOffer(RequestType.TRANSPORT) }))
			}
		}
	})

	function sendCounterOffer() {
		if (agbAccepted && !isAnyOfferAccepted($offers)) {
			let url
			// only change url to the selected pop if we selected to send an offer to the given material request
			if (materialSelected) {
				// use designatedEntity when it is selected, so not null or undefined; by default use the requestedEntityType
				url = `/api/optimizer/request/${requestPublicId}/requested/${$offerDesignatedEntity?.type ?? requestedEntityType}/${$offerDesignatedEntity?.id ?? requestedEntityId}/user/${offerUserPublicId}/updateCounterOffer`
			} else {
				url = `/api/optimizer/request/${requestPublicId}/requested/${requestedEntityType}/${requestedEntityId}/user/${offerUserPublicId}/updateCounterOffer`
			}

			let offerMat: OfferDto | null = null
			let offerMatWithTransport: OfferDto | null = null
			let offerTransport: OfferDto | null = null

			if (materialSelected) {
				offerMat = {
					facts: $offers.material.facts.toDto(),
					furtherDetailsComment: $offers.material.furtherDetailsComment,
					offerLocation: $offers.material.offerLocation,
					offerLocationId: $offers.material.offerLocationId,
					createPopMaterial: createPopMaterial
				}
			}

			if (materialWithTranspSelected) {
				offerMatWithTransport = {
					facts: $offers.materialWithTransport.facts.toDto(),
					furtherDetailsComment: $offers.materialWithTransport.furtherDetailsComment
				}
			}

			if (transportSelected) {
				offerTransport = {
					facts: $offers.transport.facts.toDto(),
					furtherDetailsComment: $offers.transport.furtherDetailsComment
				}
			}

			let payload: UpdateOfferRequest = {
				offerMaterial: offerMat,
				offerMaterialWithTransport: offerMatWithTransport,
				offerTransport: offerTransport
			}

			fetchUtils
				.post(url, payload)
				.then(() => {
					counterOfferSent = true

					let message =
						$lot.calcMethod === CalcMethod.REQUEST_OFFER
							? $t('UI.optimizer.priceOffer.sent.confirmation')
							: $t('UI.optimizer.counterOffer.sent.confirmation')

					let notification = {
						message: message,
						type: NotificationType.SUCCESS,
						timeout: 0,
						dismissible: true
					}
					addNotification(notification)
					dispatch('linkAttachmentsByAccept')
					dispatch('offerChanged')
				})
				.catch(fetchUtils.catchErrorAndShowNotification())
		} else {
			let notification = DefaultNotification(
				$t('UI.TermsAndConditions.notAccepted.error'),
				NotificationType.ERROR,
				true,
				0
			)
			addNotification(notification)
		}
	}
</script>

<div class="offer-facts-form__container">
	<div class="offer-facts-form__title">
		<h2>
			{#if $lot.calcMethod === CalcMethod.REQUEST_OFFER}
				{$t('UI.optimizerPage.priceOffer.editor.title')}
			{:else}
				{$t('UI.optimizerPage.counterOffer.editor.title')}
			{/if}
		</h2>
	</div>
	<div class="offer-facts-form__data">
		{#if $request && $request?.types?.includes(RequestType.TRANSPORT)}
			<div class="offer-transport offer__modality">
				<OfferTypeCheckbox
					bind:checked={transportSelected}
					requestType={RequestType.TRANSPORT}
					calcMethod={$lot.calcMethod}
				/>
			</div>
			<div class="offer-transport offer-facts-form__data--offer">
				<OfferFactsCard bind:offer={$offers.transport} on:editOffer={checkTransport} />
				<OfferPriceCard
					bind:offer={$offers.transport}
					calcMethod={$lot.calcMethod}
					on:editOffer={checkTransport}
				/>
			</div>
			<div class="offer-transport offer__comment">
				<TextArea
					id="furtherDetailsTransport"
					bind:text={$offers.transport.furtherDetailsComment}
					title={$t('UI.Model.moreDetails')}
					placeholder={$t('UI.remarks.putInDetailsHere')}
					marginTop="0"
				/>
			</div>
		{/if}
		{#if $request && $request?.types?.includes(RequestType.MATERIAL_WITH_TRANSPORT)}
			<div class="offer-with-transport offer__modality">
				<OfferTypeCheckbox
					bind:checked={materialWithTranspSelected}
					requestType={RequestType.MATERIAL_WITH_TRANSPORT}
					calcMethod={$lot.calcMethod}
				/>
			</div>
			<div class="offer-with-transport offer-facts-form__data--offer">
				<OfferFactsCard
					bind:offer={$offers.materialWithTransport}
					on:editOffer={checkWithTransport}
				/>
				<OfferPriceCard
					bind:offer={$offers.materialWithTransport}
					calcMethod={$lot.calcMethod}
					on:editOffer={checkWithTransport}
				/>
			</div>
			<div class="offer-with-transport offer__comment">
				<TextArea
					id="furtherDetailsMaterialWithTransport"
					bind:text={$offers.materialWithTransport.furtherDetailsComment}
					title={$t('UI.Model.moreDetails')}
					placeholder={$t('UI.remarks.putInDetailsHere')}
					marginTop="0"
				/>
			</div>
		{/if}
		{#if $request && $request?.types?.includes(RequestType.MATERIAL)}
			<div class="offer-material offer__modality">
				<OfferTypeCheckbox
					bind:checked={materialSelected}
					requestType={RequestType.MATERIAL}
					calcMethod={$lot.calcMethod}
				/>
			</div>
			<div class="offer-material offer-facts-form__data--offer">
				<OfferFactsCard bind:offer={$offers.material} on:editOffer={checkMaterial} />
				<OfferPriceCard
					bind:offer={$offers.material}
					calcMethod={$lot.calcMethod}
					on:editOffer={checkMaterial}
				/>
			</div>
			<div class="offer-material offer__place">
				<OfferFactsPlace bind:offer={$offers.material} on:editOffer={checkMaterial} />
			</div>
			{#if hasPermissions(UserPermission.ADD_PROFILE_VIA_OFFER, $jwt.perms)}
				<div class="offer-material offer__checkbox__container">
					<div class="offer__checkbox">
						<Checkbox
							id="createMaterialInPop"
							bind:checked={createPopMaterial}
							disabled={!createPopMaterialAllowed}
							label={createPopMaterialLabel}
							labelFontProps={DefaultLabelFontProps('normal', 500, '0.875rem')}
							on:checkboxChange
						/>
					</div>
				</div>
			{/if}
			<div class="offer-material offer__comment">
				<TextArea
					id="furtherDetailsMaterial"
					bind:text={$offers.material.furtherDetailsComment}
					title={$t('UI.Model.moreDetails')}
					placeholder={$t('UI.remarks.putInDetailsHere')}
					marginTop="0"
				/>
			</div>
		{/if}
	</div>
	<div class="offer-facts-form__agb">
		<AgbButton
			bind:agbAccepted
			hideCheckbox={hideAgbCheckbox}
			label={$lot.calcMethod === CalcMethod.NORMAL
				? $t('UI.optimizerPage.buttons.counteroffer.create')
				: $t('UI.optimizerPage.buttons.offer')}
			color={$lot.calcMethod === CalcMethod.NORMAL
				? PrimaryBtnColors.ORANGE
				: PrimaryBtnColors.GREEN}
			description={$lot.calcMethod === CalcMethod.NORMAL
				? $t('UI.counteroffer.optimizer.terms.description')
				: $t('UI.offer.optimizer.terms.description')}
			disabledCheckbox={!materialWithTranspSelected && !materialSelected && !transportSelected}
			on:click={sendCounterOffer}
		/>
	</div>
	<div class="offer-facts-form__buttons">
		<div class="mr-3">
			<SecondaryButton
				color={SecondaryBtnColors.GREY}
				label={$t('UI.button.cancel')}
				on:click={() => dispatch('cancelEdit')}
			/>
		</div>
	</div>
</div>

<style lang="scss">
	@use '$styles/global';

	.offer-facts-form__container {
		@include global.flex-col;

		margin-top: 1.875rem;

		& > div {
			width: 100%;
		}
	}

	.offer-facts-form__title {
		margin: 0 0 1rem;
		text-align: left;
	}

	.offer-facts-form__data {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1.375rem 7rem;
		align-items: start;
	}

	/* Transport type: ensure row 2 has equal height for both columns */
	.offer-facts-form__data:has(.offer-transport.offer-facts-form__data--offer) {
		align-items: stretch;
		column-gap: 2rem;
	}

	.offer-facts-form__data--offer {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1.375rem 2rem;
	}

	.offer-facts-form__agb {
		margin-top: 1.875rem;
	}

	.offer-facts-form__buttons {
		@include global.flex-row($justify: flex-end);
	}

	/* order of the rows for desktop, not important for mobile since we have a one-row layout there */
	@media screen and (min-width: global.$medium) {
		.offer-with-transport,
		.offer-transport {
			grid-column: 1 / 2;
		}

		.offer-material {
			grid-column: 2 / 2;
		}

		.offer__modality {
			grid-row: 1 / 2;
		}

		.offer-facts-form__data--offer {
			grid-row: 2 / 2;
		}

		/* Transport type: offer cards only take column 1 */
		.offer-transport.offer-facts-form__data--offer {
			grid-column: 1 / 2;
			align-self: start;
			display: grid;
		}

		.offer__checkbox {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			justify-content: center;
		}

		.offer__checkbox__container {
			grid-row: 4 / 4;
		}

		.offer__place {
			grid-row: 3 / 3;
		}

		.offer__comment {
			grid-row: 5 / 5;
		}

		/* Transport type: comment moves to the right side (column 2) on the same row as offer cards (row 2) */
		.offer-transport.offer__comment {
			grid-row: 2 / 2;
			grid-column: 2 / 2;
			align-self: stretch;
			display: flex;
			flex-direction: column;
			height: 100%;

			:global(.input-field-error-message-container) {
				flex: 1;
				display: flex;
				flex-direction: column;
				min-height: 0;
				height: 100%;
			}

			:global(.textarea-container) {
				flex: 1;
				display: flex;
				flex-direction: column;
				margin-top: -2.1rem;
				min-height: 0;
				height: calc(100% + 2rem);
			}

			:global(.textarea-container > span:first-child) {
				flex-shrink: 0;
			}

			:global(textarea) {
				flex: 1;
			}
		}
	}

	/* Tablets */
	@media screen and (max-width: global.$medium) {
		.offer-facts-form__data {
			grid-template-columns: 1fr;
		}

		.offer-facts-form__data--offer {
			grid-template-columns: 1fr 1fr;
		}

		.offer-with-transport.offer__modality,
		.offer-transport.offer__modality {
			margin-top: 1.375rem;
		}
	}

	/* Phones */
	@media screen and (max-width: global.$small) {
		.offer-facts-form__data--offer {
			grid-template-columns: 1fr;
		}
	}
</style>
